/**
 * The Files browser sidebar — the list of mounts this caller can see.
 *
 * Each mount is a navigation target (click → list its root) and a drop target
 * (drag a row onto a writable mount → move the file into that mount's root).
 * Read-only mounts (Docs, Repo, Meetings) badge as such and refuse drops.
 */

import type { DragEvent, ReactElement } from 'react';
import { type VfsMountInfo } from '../shared/api-client';
import { FileIconComponent, fileIconKindFor } from './FileIconComponent';
import './FilesSidebarComponent.css';

export interface FilesSidebarComponentProps {
  mounts: VfsMountInfo[];
  /** Frozen mirror paths, so a synced folder can be badged in the sidebar too. */
  syncSourcePaths: Set<string>;
  /** Absolute path of the current directory, to mark the active mount. */
  currentDir: string | undefined;
  /** Navigate to a mount root or a folder. */
  onNavigate: (dir: string) => void;
  /** Move a set of dragged entries into the target directory. */
  onDropMove: (fromPaths: string[], toDir: string) => void;
  dragActive?: boolean;
}

/** A human label for each mount, used by the sidebar and the breadcrumbs. */
export function mountLabel(mountPath: string): string {
  const seg = mountPath.slice(1);
  // /library → Library, /work → This chat, /attachments → Attachments, etc.
  if (seg === 'library') return 'Library';
  if (seg === 'work') return 'This chat';
  if (seg === 'attachments') return 'Attachments';
  if (seg === 'docs') return 'Docs';
  if (seg === 'repo') return 'Code';
  if (seg === 'meetings') return 'Meetings';
  if (seg.startsWith('projects/')) return seg.slice('projects/'.length);
  if (seg.startsWith('account/')) return seg.slice('account/'.length);
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

export function FilesSidebarComponent({
  mounts,
  syncSourcePaths,
  currentDir,
  onNavigate,
  onDropMove,
  dragActive,
}: FilesSidebarComponentProps): ReactElement {
  const handleDrop = (e: DragEvent<HTMLButtonElement>, toDir: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('files-sidebar-item--drag-over');
    const raw = e.dataTransfer.getData('text/vfs-paths') || e.dataTransfer.getData('text/vfs-path');
    if (!raw) return;
    const fromPaths = raw.split('\n').filter((p) => p && p !== toDir && !p.startsWith(`${toDir}/`));
    if (fromPaths.length > 0) onDropMove(fromPaths, toDir);
  };

  const writable = mounts.filter((m) => m.writable);
  const readonly = mounts.filter((m) => !m.writable);

  const renderItem = (mount: VfsMountInfo): ReactElement => {
    const active = currentDir !== undefined
      && (currentDir === mount.path || currentDir.startsWith(`${mount.path}/`));
    const frozen = syncSourcePaths.has(mount.path);
    return (
      <button
        key={mount.path}
        type="button"
        className={`files-sidebar-item${active ? ' files-sidebar-item--active' : ''}${dragActive && mount.writable ? ' files-sidebar-item--droppable' : ''}`}
        onClick={() => onNavigate(mount.path)}
        onDragOver={(e) => { if (dragActive && mount.writable && (e.dataTransfer.types.includes('text/vfs-paths') || e.dataTransfer.types.includes('text/vfs-path'))) e.preventDefault(); }}
        onDragEnter={(e) => { if (dragActive && mount.writable) e.currentTarget.classList.add('files-sidebar-item--drag-over'); }}
        onDragLeave={(e) => e.currentTarget.classList.remove('files-sidebar-item--drag-over')}
        onDrop={(e) => mount.writable && handleDrop(e, mount.path)}
        title={mount.description}
      >
        <FileIconComponent kind={frozen ? 'folder' : fileIconKindFor('directory', mount.path, mount.path)} size={16} />
        <span className="files-sidebar-item-label">{mountLabel(mount.path)}</span>
        {!mount.writable && <span className="files-sidebar-item-badge">read-only</span>}
        {frozen && <span className="files-sidebar-item-badge files-sidebar-item-badge--sync">synced</span>}
      </button>
    );
  };

  return (
    <aside className="files-sidebar">
      {writable.length > 0 && (
        <section className="files-sidebar-section">
          <h2 className="files-sidebar-heading">Your files</h2>
          {writable.map(renderItem)}
        </section>
      )}
      {readonly.length > 0 && (
        <section className="files-sidebar-section">
          <h2 className="files-sidebar-heading">Shared & read-only</h2>
          {readonly.map(renderItem)}
        </section>
      )}
    </aside>
  );
}
